import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ASYNC_TASK_NAMES } from "@performyard/models";
import { Text, formatDateTime } from "@performyard/py-components";
import {
	MetaDataReviewCycleInsightResponseStreamJson,
	MetaDataReviewInsightResponseStreamJson,
	ReviewCycleInsightResponseStreamJson,
	UserErrorResponse,
	UserErrorResponseJson,
} from "@performyard/service-contracts";

import { useCompanyAsyncTask } from "../../../hooks/useCompanyAsyncTask";

import { postQueryAi } from "../../../api/services/defs/internal-api";
import { getActiveCompanyTasksByName } from "../../../api/services/defs/internal-api/async-tasks";

import { InsightsGeneratedAt } from "../InsightsGeneratedAt";
import { AISidePanel } from "../SidePanel";

export type SummarizeReviewCycleModuleProps = {
	reviewCycleId: string;
	authorCompletedReviewCount: number;
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
};

export type StreamedData = {
	data: ReviewCycleInsightResponseStreamJson | UserErrorResponse;
	inProgress: boolean;
} | null;

type ComponentPhase =
	| "idle"
	| "checking_cache"
	| "creating_task"
	| "streaming"
	| "fetching_result"
	| "complete"
	| "error";

const MINIMUM_REVIEWS_REQUIRED = 2;
const INSUFFICIENT_REVIEWS_ERROR =
	"Insights are not available for this review cycle.\n\nAt least two reviews must have a form completed by an author before insights can be generated.";

export const SummarizeReviewCycleModule = ({
	reviewCycleId,
	authorCompletedReviewCount,
	isOpen,
	onOpenChange,
}: SummarizeReviewCycleModuleProps): React.ReactElement => {
	const [phase, setPhase] = useState<ComponentPhase>("idle");
	const [cachedInsight, setCachedInsight] = useState<MetaDataReviewCycleInsightResponseStreamJson | null>(null);
	const [initialTaskData, setInitialTaskData] = useState<MetaDataReviewCycleInsightResponseStreamJson | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const hasInsufficientReviews = authorCompletedReviewCount < MINIMUM_REVIEWS_REQUIRED;

	// Track if we're in a retry flow to skip cache check
	const isRetryingRef = useRef(false);
	// Track the current operation to prevent stale callbacks
	const operationIdRef = useRef(0);

	const {
		task,
		triggerTask,
		error: taskError,
	} = useCompanyAsyncTask(ASYNC_TASK_NAMES.GENERATE_REVIEW_CYCLE_INSIGHTS);

	/**
	 * Creates a new insight generation task
	 * @param isRetry - Whether this is a retry/regenerate request
	 */
	const createTask = useCallback(
		async (isRetry: boolean): Promise<void> => {
			const currentOperationId = ++operationIdRef.current;

			setPhase("creating_task");
			setCachedInsight(null);
			setInitialTaskData(null);
			setErrorMessage(null);

			try {
				await triggerTask(async () => {
					const response = await postQueryAi<
						MetaDataReviewCycleInsightResponseStreamJson | UserErrorResponseJson
					>({
						type: "review-cycle-insight-haiku",
						reviewCycleId,
						findAdditionalContext: false,
						retry: isRetry ? "true" : "false",
						responseStyle: "structured",
					});

					// Check if this operation is still current
					if (currentOperationId !== operationIdRef.current) {
						throw new Error("Operation cancelled");
					}

					if ("message" in response) {
						throw new Error(response.message);
					}
					if (!response.taskId) {
						throw new Error("No taskId returned from AI service");
					}

					// Store the initial response data for progress display
					if (response.status === "in_progress") {
						setInitialTaskData(response);
					}

					setPhase("streaming");
					return { taskId: response.taskId };
				});
			} catch (error) {
				// Only update state if this is still the current operation
				if (currentOperationId === operationIdRef.current) {
					const message = error instanceof Error ? error.message : "Failed to create insight task";
					if (message !== "Operation cancelled") {
						setErrorMessage(message);
						setPhase("error");
					}
				}
				throw error;
			}
		},
		[reviewCycleId, triggerTask]
	);

	/**
	 * Fetches the completed insight from the API
	 */
	const fetchCompletedInsight = useCallback(async (): Promise<void> => {
		const currentOperationId = operationIdRef.current;

		setPhase("fetching_result");

		try {
			const response = await postQueryAi<MetaDataReviewCycleInsightResponseStreamJson | UserErrorResponseJson>({
				type: "review-cycle-insight-haiku",
				reviewCycleId,
				findAdditionalContext: false,
				retry: "false",
				responseStyle: "structured",
			});

			// Check if this operation is still current
			if (currentOperationId !== operationIdRef.current) {
				return;
			}

			if ("message" in response) {
				setErrorMessage(response.message);
				setPhase("error");
				return;
			}

			// Check if this is a valid completed insight
			const hasProcessingState =
				response.data && typeof response.data === "object" && "processing" in response.data;

			if (response.isCached || response.status === "completed" || !hasProcessingState) {
				setCachedInsight({
					...response,
					status: "completed" as const,
				});
				setPhase("complete");
			} else {
				// Still processing, stay in streaming phase
				setPhase("streaming");
			}
		} catch (error) {
			if (currentOperationId === operationIdRef.current) {
				setErrorMessage(error instanceof Error ? error.message : "Failed to fetch insight");
				setPhase("error");
			}
		}
	}, [reviewCycleId]);

	/**
	 * Check for existing insight or active task when panel opens
	 */
	useEffect(() => {
		if (!isOpen) {
			return;
		}

		// Skip cache check if we're in a retry flow
		if (isRetryingRef.current) {
			isRetryingRef.current = false;
			return;
		}

		const checkInsightStatus = async (): Promise<void> => {
			const currentOperationId = ++operationIdRef.current;

			setPhase("checking_cache");
			setErrorMessage(null);

			try {
				// Step 1: Check for active tasks for this review cycle
				// This handles the case where user closes/reopens panel while task is in progress
				const activeTasks = await getActiveCompanyTasksByName(ASYNC_TASK_NAMES.GENERATE_REVIEW_CYCLE_INSIGHTS);

				// Check if operation is still current
				if (currentOperationId !== operationIdRef.current) {
					return;
				}

				// Find a task matching this reviewCycleId
				const matchingTask = activeTasks.find((t) => {
					const metadata = t.metadata as { reviewCycleId?: string } | undefined;
					return metadata?.reviewCycleId === reviewCycleId;
				});

				if (matchingTask) {
					// Active task found - connect to SSE
					setPhase("streaming");
					await triggerTask(() => Promise.resolve({ taskId: matchingTask._id }));
					return;
				}

				// Step 2: No active task - check for cached/completed insight
				const response = await postQueryAi<
					MetaDataReviewCycleInsightResponseStreamJson | UserErrorResponseJson
				>({
					type: "review-cycle-insight-haiku",
					reviewCycleId,
					findAdditionalContext: false,
					retry: "false",
					responseStyle: "structured",
				});

				// Check if operation is still current
				if (currentOperationId !== operationIdRef.current) {
					return;
				}

				// Handle error response - no cached insight exists
				if ("message" in response) {
					// Check if we have enough reviews before attempting to create a new task
					if (hasInsufficientReviews) {
						setErrorMessage(INSUFFICIENT_REVIEWS_ERROR);
						setPhase("error");
						return;
					}
					// Create a new task
					await createTask(false);
					return;
				}

				// Completed insight exists
				if (response.status === "completed" && response.data?.insight) {
					setCachedInsight(response);
					setPhase("complete");
					return;
				}

				// In progress with taskId - this shouldn't happen since we checked active tasks first,
				// but handle it just in case
				if (response.status === "in_progress" && response.taskId) {
					setInitialTaskData(response);
					setPhase("streaming");
					await triggerTask(() => Promise.resolve({ taskId: response.taskId! }));
					return;
				}

				// No completed insight and no active task - check if we have enough reviews
				if (hasInsufficientReviews) {
					setErrorMessage(INSUFFICIENT_REVIEWS_ERROR);
					setPhase("error");
					return;
				}
				// Create new one
				await createTask(false);
			} catch (error) {
				if (currentOperationId === operationIdRef.current) {
					console.error("Error checking insight status:", error);
					setErrorMessage(error instanceof Error ? error.message : "Failed to check insight status");
					setPhase("error");
				}
			}
		};

		checkInsightStatus();
	}, [isOpen, reviewCycleId, createTask, triggerTask, hasInsufficientReviews]);

	/**
	 * Handle task status changes from SSE
	 */
	const taskStatus = task?.status;
	useEffect(() => {
		if (taskStatus === "completed" && phase === "streaming") {
			fetchCompletedInsight();
		} else if (taskStatus === "failed" && phase === "streaming") {
			setErrorMessage(task?.error || "Task failed");
			setPhase("error");
		} else if ((taskStatus === "processing" || taskStatus === "queued") && phase === "creating_task") {
			setPhase("streaming");
		}
	}, [taskStatus, phase, task?.error, fetchCompletedInsight]);

	/**
	 * Handle retry/regenerate button click
	 */
	const handleRetry = useCallback(async (): Promise<void> => {
		if (hasInsufficientReviews) {
			setErrorMessage(INSUFFICIENT_REVIEWS_ERROR);
			setPhase("error");
			return;
		}
		isRetryingRef.current = true;
		await createTask(true);
	}, [createTask, hasInsufficientReviews]);

	/**
	 * Construct display data from task or cached insight
	 */
	const displayData = useMemo<MetaDataReviewCycleInsightResponseStreamJson | null>(() => {
		// Show cached/completed insight
		if (cachedInsight && phase === "complete") {
			return cachedInsight;
		}

		// Extract totalReviews from task metadata, or derive from progressTotal
		const getTotalReviews = (): number => {
			const metadataTotalReviews = (task?.metadata as { totalReviews?: number })?.totalReviews;
			if (metadataTotalReviews) return metadataTotalReviews;
			if (task?.progressTotal) return Math.ceil((task.progressTotal + 1) / 2);
			return 1;
		};

		type ProgressData = {
			processing: boolean;
			totalChunks: number;
			completedChunks: number;
			totalReviews: number;
		};

		// Show progress from SSE task
		if (task && (task.status === "processing" || task.status === "queued" || task.status === "completed")) {
			const progressData: ProgressData = {
				processing: true,
				totalChunks: task.progressTotal || 1,
				completedChunks: task.status === "completed" ? task.progressTotal || 1 : task.progressCount,
				totalReviews: getTotalReviews(),
			};
			return {
				status: "in_progress" as const,
				data: progressData as unknown as MetaDataReviewCycleInsightResponseStreamJson["data"],
				promptHash: "",
				isCached: false,
				createdAt: task.createdAt,
				userId: initialTaskData?.userId,
				createdBy: initialTaskData?.createdBy,
			};
		}

		// Fall back to initial task data (before SSE updates arrive)
		if (initialTaskData && initialTaskData.status === "in_progress") {
			return initialTaskData;
		}

		return null;
	}, [task, cachedInsight, initialTaskData, phase]);

	/**
	 * Determine if we should show loading state
	 */
	const isLoading = useMemo((): boolean => {
		switch (phase) {
			case "checking_cache":
			case "creating_task":
				return true;
			case "streaming":
			case "fetching_result":
				return true;
			case "complete":
			case "error":
			case "idle":
				return false;
			default:
				return false;
		}
	}, [phase]);

	/**
	 * Determine if we should show the donut chart (chunking progress)
	 */
	const showDonutProgress = useMemo((): boolean => {
		// Show donut when streaming and we have progress data
		if ((phase === "streaming" || phase === "fetching_result") && displayData) {
			return true;
		}
		return false;
	}, [phase, displayData]);

	/**
	 * Check if response indicates missing or malformed insight data.
	 * This is a safety net for edge cases - the main validation for insufficient reviews
	 * happens before any API calls are made.
	 */
	const checkForInsightError = useCallback(
		(
			response:
				| UserErrorResponseJson
				| MetaDataReviewInsightResponseStreamJson
				| MetaDataReviewCycleInsightResponseStreamJson
				| undefined
		): UserErrorResponseJson | null => {
			if (!response) return null;
			if (phase !== "complete" && phase !== "error") return null;

			const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object";

			// Check if completed but no insight data (malformed response)
			if (isObj(response) && "status" in response && response.status === "completed") {
				const responseData = isObj(response.data) ? response.data : null;

				const hasNestedInsight = !!responseData && "insight" in responseData && responseData.insight != null;
				const hasDirectInsightFields =
					!!responseData &&
					("summary" in responseData ||
						"strengthsAnalysis" in responseData ||
						"opportunitiesAnalysis" in responseData ||
						"highlightedReviews" in responseData);
				const hasInsight = hasNestedInsight || hasDirectInsightFields;

				if (!hasInsight) {
					return {
						message: "Unable to display insights. The response data may be incomplete or malformed.",
						details: [],
					};
				}
			}

			return null;
		},
		[phase]
	);

	/**
	 * Compute the error to display
	 */
	const displayError = useMemo((): UserErrorResponseJson | null => {
		// Check for insight-specific errors
		const insightError = checkForInsightError(displayData ?? undefined);
		if (insightError) return insightError;

		// Check for explicit error message
		if (errorMessage) {
			return { message: errorMessage, details: [] };
		}

		// Check for task error from SSE
		if (task?.status === "failed") {
			return {
				message: task.error || "An error occurred while generating insights. Please try again.",
				details: [],
			};
		}

		// Check for hook-level error
		if (taskError) {
			return { message: taskError.message, details: [] };
		}

		return null;
	}, [displayData, errorMessage, task?.status, task?.error, taskError, checkForInsightError]);

	const renderHeader = (data: ReviewCycleInsightResponseStreamJson, isChunking: boolean): React.ReactNode => (
		<div className="border-radius-1 flex w-full flex-col items-center justify-center gap-4 self-stretch border-b border-solid border-neutral-200 pb-4">
			<div className="flex items-start gap-5 self-stretch rounded bg-neutral-100 p-2">
				<div className="flex flex-1 shrink-0 basis-0 flex-col items-start gap-2">
					<Text size="paragraph-1" weight="semi-bold" color="neutral-9">
						{isChunking ? "Review Cycle Insights" : data?.title || ""}
					</Text>

					<div className="flex flex-col items-start gap-1">
						{isChunking ? (
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								We&apos;re currently analyzing each review to create your insights. This may take a few
								minutes. Feel free to close this panel and navigate away from this page, and check back
								later.
							</Text>
						) : (
							<>
								<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
									Review Started:{" "}
									<Text size="paragraph-3" weight="regular" color="neutral-9" overrideElement="span">
										{data.startDate && Date.parse(data.startDate)
											? formatDateTime(new Date(data.startDate), {
													hasTime: true,
													dateSize: "medium",
												})
											: (data.startDate ?? "Not started")}
									</Text>
								</Text>
								{data?.completedDate && (
									<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
										Completed:{" "}
										<Text
											size="paragraph-3"
											weight="regular"
											color="neutral-9"
											overrideElement="span"
										>
											{data.completedDate && Date.parse(data.completedDate)
												? formatDateTime(new Date(data.completedDate), {
														hasTime: true,
														dateSize: "medium",
													})
												: (data.completedDate ?? "Not completed")}
										</Text>
									</Text>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);

	const renderInfoSection = (): React.ReactNode => {
		if (!displayData || displayError) return null;

		const generatedBy =
			"createdBy" in displayData
				? displayData.createdBy
				: "data" in displayData && displayData.data
					? (displayData.data as Record<string, unknown>).createdBy
					: null;

		return (
			<InsightsGeneratedAt
				// createdAt is the task/log creation time, i.e. when generation started. AISidePanel renders
				// this line during the chunking progress state too, so it only reads as "populated" once the
				// insight is actually complete.
				label={displayData.status === "in_progress" ? "Insights Started" : "Insights Populated"}
				date={displayData.createdAt}
				generatedBy={typeof generatedBy === "string" ? generatedBy : null}
			/>
		);
	};

	return (
		<AISidePanel<MetaDataReviewCycleInsightResponseStreamJson>
			type="review-cycle"
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			data={displayData && !displayError ? displayData : null}
			isLoading={isLoading && !showDonutProgress}
			renderHeader={renderHeader}
			renderInfoSection={renderInfoSection}
			onRetry={handleRetry}
			error={displayError}
		/>
	);
};
